import React, { useEffect, useState, useContext } from "react";
import api from "../../services/AxiosConnection";
import { useNavigate } from "react-router-dom";
import { Vortex } from "react-loader-spinner";
import AlarmsDialog from "../simpleView/AlarmsDialog";
import WarningsDialog from "../simpleView/WarningsDialog";
import AuthContext from "../../context/AuthContext";
import socket from "../../services/socket";

const calculateRemark = (dateTime) => {
  if (!dateTime || dateTime === "N/A")
    return { text: "Data not available", color: "bg-gray-500" };

  const currentTime = new Date();
  const trainTime = new Date(dateTime);
  if (isNaN(trainTime)) return { text: "Invalid date", color: "bg-gray-500" };

  const currentUTCTime = new Date(
    currentTime.getTime() - currentTime.getTimezoneOffset() * 60 * 1000
  );
  const timeDiff = currentUTCTime - trainTime;
  const minutesDiff = Math.floor(timeDiff / (1000 * 60));

  if (minutesDiff <= 60) {
    return {
      text: `Train passed ${minutesDiff} minute${minutesDiff !== 1 ? "s" : ""
        } ago`,
      color: "bg-green-600",
    };
  } else if (minutesDiff > 60 && minutesDiff <= 180) {
    const hoursDiff = Math.floor(minutesDiff / 60);
    const remainingMinutes = minutesDiff % 60;
    return {
      text: `Train passed ${hoursDiff} hour${hoursDiff !== 1 ? "s" : ""
        } and ${remainingMinutes} minute${remainingMinutes !== 1 ? "s" : ""} ago`,
      color: "bg-amber-500",
    };
  } else {
    const hoursDiff = Math.floor(minutesDiff / 60);
    return {
      text: `No train passed for ${hoursDiff} hour${hoursDiff !== 1 ? "s" : ""
        }`,
      color: "bg-red-600",
    };
  }
};

const formatDateTime = (dateTime) => {
  if (!dateTime || dateTime === "N/A") return "N/A";
  const utcDate = new Date(dateTime);
  if (isNaN(utcDate)) return "Invalid date";

  const localDate = new Date(
    utcDate.getTime() + utcDate.getTimezoneOffset() * 60 * 1000
  );
  const day = String(localDate.getDate()).padStart(2, "0");
  const month = String(localDate.getMonth() + 1).padStart(2, "0");
  const year = localDate.getFullYear();
  let hours = localDate.getHours();
  const minutes = String(localDate.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${day}-${month}-${year}\n${hours}:${minutes} ${ampm}`;
};

const TrainStatusUI = () => {
  const [sites, setSites] = useState([]);
  const [divisionDetails, setDivisionDetails] = useState([]);
  const [apiTrainData, setApiTrainData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlarms, setSelectedAlarms] = useState([]);
  const [selectedWarnings, setSelectedWarnings] = useState([]);
  const [alarmKey, setAlarmKey] = useState(0);
  const [warningKey, setWarningKey] = useState(0);
  const [fetchingAlerts, setFetchingAlerts] = useState(false);
  const { setSelectedSite } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleOpenAlertPopup = async (trainId, siteDb, type) => {
    if (!trainId || !siteDb) return;
    if (setSelectedSite) {
      setSelectedSite(siteDb);
    }
    setFetchingAlerts(true);
    try {
      const response = await api.get("/simple-view/getAlertsByTrainId", {
        params: { siteDb, trainId },
      });
      const alarms = response.data?.alarms || [];
      const warnings = response.data?.warnings || [];

      if (type === "alarm") {
        if (alarms.length > 0) {
          setSelectedAlarms(alarms);
          setAlarmKey((prev) => prev + 1);
        } else {
          alert("No active alarm details recorded for this train.");
        }
      } else if (type === "warning") {
        if (warnings.length > 0) {
          setSelectedWarnings(warnings);
          setWarningKey((prev) => prev + 1);
        } else {
          alert("No active warning details recorded for this train.");
        }
      } else {
        if (alarms.length > 0) {
          setSelectedAlarms(alarms);
          setAlarmKey((prev) => prev + 1);
        }
        if (warnings.length > 0) {
          setSelectedWarnings(warnings);
          setWarningKey((prev) => prev + 1);
        }
        if (alarms.length === 0 && warnings.length === 0) {
          alert("No active alarm or warning details recorded for this train.");
        }
      }
    } catch (error) {
      console.error("Error fetching alerts for popup:", error);
    } finally {
      setFetchingAlerts(false);
    }
  };

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (user?.id) {
          const response = await api.get(`/users/get-sites`, {
            params: { userId: user.id },
          });
          setSites(response.data?.sites || []);
        }
      } catch (error) {
        console.error("Error fetching sites:", error);
        setLoading(false);
      }
    };
    fetchSites();
  }, []);

  const isFetchingDivisionRef = React.useRef(false);

  useEffect(() => {
    const fetchDivisionDetails = async () => {
      if (!sites || sites.length === 0 || isFetchingDivisionRef.current) return;
      isFetchingDivisionRef.current = true;
      try {
        const sitePromises = sites.map((site) =>
          api.get("/division/getAllSiteDetails", {
            params: { siteDbs: site.siteDbName },
          })
        );
        const results = await Promise.allSettled(sitePromises);
        const combinedResults = results
          .filter((res) => res.status === "fulfilled" && res.value?.data?.results)
          .flatMap((res) => res.value.data.results);

        setDivisionDetails(combinedResults);
      } catch (error) {
        console.error("Error fetching division details:", error);
      } finally {
        setLoading(false);
        isFetchingDivisionRef.current = false;
      }
    };
    if (sites.length > 0) fetchDivisionDetails();
  }, [sites]);

  // Socket.IO real-time listener & 10-minute interval polling without page refresh
  useEffect(() => {
    if (!sites || sites.length === 0) return;

    const siteDbsList = sites.map((site) => site.siteDbName);

    if (socket.connected) {
      socket.emit("join_division", siteDbsList);
    }

    const handleConnect = () => {
      socket.emit("join_division", siteDbsList);
    };

    const handleDivisionDataUpdated = (updatedSiteData) => {
      if (!updatedSiteData || !updatedSiteData.siteDb) return;
      setDivisionDetails((prevDetails) => {
        const index = prevDetails.findIndex(
          (item) => item.siteDb === updatedSiteData.siteDb
        );
        if (index !== -1) {
          const updated = [...prevDetails];
          updated[index] = updatedSiteData;
          return updated;
        } else {
          return [...prevDetails, updatedSiteData];
        }
      });
    };

    socket.on("connect", handleConnect);
    socket.on("division_data_updated", handleDivisionDataUpdated);

    // 10 minutes polling interval without page refresh (10 * 60 * 1000 = 600,000 ms)
    const TEN_MINUTES_MS = 10 * 60 * 1000;
    const intervalId = setInterval(() => {
      const fetchDivisionDetailsParallel = async () => {
        try {
          const sitePromises = sites.map((site) =>
            api.get("/division/getAllSiteDetails", {
              params: { siteDbs: site.siteDbName },
            })
          );
          const results = await Promise.allSettled(sitePromises);
          const combinedResults = results
            .filter((res) => res.status === "fulfilled" && res.value?.data?.results)
            .flatMap((res) => res.value.data.results);

          setDivisionDetails(combinedResults);
        } catch (err) {
          console.error("10-min division fetch error:", err);
        }
      };
      fetchDivisionDetailsParallel();
    }, TEN_MINUTES_MS);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("division_data_updated", handleDivisionDataUpdated);
      clearInterval(intervalId);
    };
  }, [sites]);

  useEffect(() => {
    if (divisionDetails.length > 0) {
      const siteDbToNameMap = {};
      sites.forEach((site) => {
        siteDbToNameMap[site.siteDbName] = site.siteName;
      });

      const processedData = divisionDetails.flatMap((division) => {
        const { siteDb, trainTransactionCount, trainTransactionDetails } =
          division;
        const latestTrainEntry = trainTransactionDetails?.[0];

        return {
          siteDb: siteDb,
          location: siteDbToNameMap[siteDb] || siteDb,
          deviceID: siteDbToNameMap[siteDb]?.split("_").pop() || "N/A",
          latestTrainDateTime: latestTrainEntry?.DateofCreation || "N/A",
          trains:
            trainTransactionDetails?.map((train, index) => ({
              trainId: train.TrainId,
              trainCount: `Train${trainTransactionCount - index}`,
              dateTime: train.DateofCreation || "N/A",
              totalAxle: train.TotalNoOfAxles,
              lhMaxTemp: train.MaxLHTemperature,
              rhMaxTemp: train.MaxRHTemperature,
              tempDiff: Math.abs(
                train.MaxLHTemperature - train.MaxRHTemperature
              ).toFixed(1),
              tempDiffIn: train.Direction,
              alarmCount: train.AlarmCount || 0,
              warningCount: train.WarningCount || 0,
            })) || [],
        };
      });

      setApiTrainData(processedData);
    }
  }, [divisionDetails, sites]);

  return (
    <div className="bg-slate-50 min-h-screen font-sans overflow-x-hidden mt-24 md:mt-0">
      {(loading || fetchingAlerts) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <Vortex
            visible={true}
            height={80}
            width={80}
            ariaLabel="vortex-loading"
            wrapperStyle={{}}
            wrapperClass="vortex-wrapper"
            colors={[
              "#2563eb",
              "#1e40af",
              "#60a5fa",
              "#3b82f6",
              "#1d4ed8",
              "#93c5fd",
            ]}
          />
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-lg shadow-lg p-3 mb-4">
          <h1 className="text-xl md:text-2xl font-bold text-center text-white">
            Train Status Monitoring
          </h1>
        </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-slate-200">
          <div className="overflow-x-auto sm:overflow-x-visible">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gradient-to-r from-blue-900 to-blue-800 text-white text-xs text-center">
                  <th className="p-2 font-semibold border-b border-blue-700">
                    Location
                  </th>
                  <th className="p-2 font-semibold border-b border-blue-700">
                    Device ID
                  </th>
                  <th className="p-2 font-semibold border-b border-blue-700">
                    Train Count
                  </th>
                  <th className="p-2 font-semibold border-b border-blue-700 w-40">
                    Date & Time
                  </th>
                  <th className="p-2 font-semibold border-b border-blue-700">
                    Total Axle
                  </th>
                  <th className="p-2 font-semibold border-b border-blue-700 w-24">
                    LH Max Temp(°C)
                  </th>
                  <th className="p-2 font-semibold border-b border-blue-700 w-24">
                    RH Max Temp(°C)
                  </th>
                  <th className="p-2 font-semibold border-b border-blue-700 w-24">
                    Temp Diff(°C)
                  </th>
                  <th className="p-2 font-semibold border-b border-blue-700">
                    Train Dir.
                  </th>
                  <th className="p-2 font-semibold border-b border-blue-700">
                    Remark
                  </th>
                  <th className="p-2 font-semibold border-b border-blue-700">
                    Alerts
                  </th>
                  <th className="p-2 font-semibold border-b border-blue-700">
                    Log
                  </th>
                </tr>
              </thead>
              <tbody>
                {apiTrainData.map((locationData, index) => (
                  <React.Fragment key={index}>
                    <tr className="hover:bg-blue-50">
                      <td className="p-2 text-xs font-bold text-blue-950 border-b">
                        {locationData.location}
                      </td>
                      <td className="p-2 text-xs font-bold text-blue-950 border-b">
                        {locationData.deviceID}
                      </td>
                      <td className="p-2 border-b"></td>
                      <td className="p-2 border-b"></td>
                      <td className="p-2 border-b"></td>
                      <td className="p-2 border-b"></td>
                      <td className="p-2 border-b"></td>
                      <td className="p-2 border-b"></td>
                      <td className="p-2 border-b"></td>
                      <td className="p-2 text-xs border-b text-center">
                        <span
                          className={`${calculateRemark(locationData.latestTrainDateTime)
                              .color
                            } text-white px-2 py-1 rounded-full text-xs inline-block w-full`}
                        >
                          {
                            calculateRemark(locationData.latestTrainDateTime)
                              .text
                          }
                        </span>
                      </td>
                      <td className="p-2 border-b"></td>
                      <td className="p-2 border-b"></td>
                    </tr>

                    {locationData.trains.map((train, trainIndex) => (
                      <tr
                        key={`${index}-${trainIndex}`}
                        className="bg-slate-100/70 hover:bg-slate-50"
                      >
                        <td className="p-2 border-b text-xs"></td>
                        <td className="p-2 border-b text-xs"></td>
                        <td className="p-2 border-b text-xs text-center font-medium text-slate-700">
                          {train.trainCount}
                        </td>
                        <td className="p-2 border-b text-xs text-center whitespace-pre-line">
                          <div className="bg-white px-3 py-1 rounded-md border border-slate-200 inline-block w-32">
                            {formatDateTime(train.dateTime)}
                          </div>
                        </td>
                        <td className="p-2 border-b text-xs text-center">
                          {train.totalAxle}
                        </td>
                        <td className="p-2 border-b text-xs text-center text-slate-700">
                          {train.lhMaxTemp
                            ? `${train.lhMaxTemp} °C`
                            : train.lhMaxTemp}
                        </td>
                        <td className="p-2 border-b text-xs text-center text-slate-700">
                          {train.rhMaxTemp
                            ? `${train.rhMaxTemp} °C`
                            : train.rhMaxTemp}
                        </td>
                        <td className="p-2 border-b text-xs text-center">
                          {train.tempDiff ? (
                            <span className="bg-amber-50 px-2 py-1 rounded-md inline-block text-slate-700">
                              {train.tempDiff} °C
                            </span>
                          ) : (
                            train.tempDiff
                          )}
                        </td>
                        <td className="p-2 border-b text-xs text-center text-blue-700 bg-blue-50 rounded-md">
                          {train.tempDiffIn}
                        </td>
                        <td className="p-2 border-b text-xs text-center"></td>
                        <td className="p-2 border-b text-xs text-center">
                          <div className="inline-flex items-center justify-center gap-1 font-semibold text-xs px-2 py-1 rounded border border-slate-200 bg-white shadow-sm">
                            <button
                              onClick={() =>
                                handleOpenAlertPopup(
                                  train.trainId,
                                  locationData.siteDb,
                                  "alarm"
                                )
                              }
                              className={
                                train.alarmCount > 0
                                  ? "bg-red-600 hover:bg-red-700 text-white px-1.5 py-0.5 rounded font-bold cursor-pointer transition-colors"
                                  : "text-slate-600 font-medium cursor-pointer hover:text-slate-900"
                              }
                              title="Click to view alarms popup"
                            >
                              A:{train.alarmCount || 0}
                            </button>
                            <button
                              onClick={() =>
                                handleOpenAlertPopup(
                                  train.trainId,
                                  locationData.siteDb,
                                  "warning"
                                )
                              }
                              className={
                                train.warningCount > 0
                                  ? "bg-amber-500 hover:bg-amber-600 text-white px-1.5 py-0.5 rounded font-bold cursor-pointer transition-colors"
                                  : "text-slate-600 font-medium cursor-pointer hover:text-slate-900"
                              }
                              title="Click to view warnings popup"
                            >
                              W:{train.warningCount || 0}
                            </button>
                          </div>
                        </td>
                        <td className="p-2 border-b text-xs text-center">
                          <button
                            className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 text-xs rounded-md"
                            onClick={() =>
                              navigate("/dashboard/simple-view", {
                                state: {
                                  trainId: train.trainId,
                                  siteDb: locationData.siteDb,
                                },
                              })
                            }
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <AlarmsDialog key={`alarm-${alarmKey}`} alarms={selectedAlarms} />
      <WarningsDialog key={`warning-${warningKey}`} warnings={selectedWarnings} />
    </div>
  );
};

export default TrainStatusUI;
